import React, { useId, useMemo } from 'react';
import InternationalPhoneInput, { Country, getCountries } from 'react-phone-number-input';
import cx from 'classnames';

import { signupLanguage } from 'ui-brand/languages';

import FieldLabel from './FieldLabel';
import PhoneCountrySelect from './PhoneCountrySelect';

type Props = {
  label: string;
  optionalHint?: string;
  country: Country | undefined;
  onCountryChange: (country?: Country) => void;
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

function PhoneInput({ label, optionalHint, country, onCountryChange, value, onChange, error }: Props) {
  const inputId = useId();
  const errorId = `${inputId}-error`;
  const language = signupLanguage();
  const labels = useMemo(() => {
    const names = new Intl.DisplayNames([language.code], { type: 'region' });
    return {
      ...Object.fromEntries(getCountries().map((code) => [code, names.of(code)])),
      ZZ: gettext('International'),
    };
  }, [language.code]);

  return (
    <div className="w-full font-lexend">
      <FieldLabel htmlFor={inputId} optionalHint={optionalHint}>
        {label}
      </FieldLabel>
      <InternationalPhoneInput
        id={inputId}
        defaultCountry={country}
        onCountryChange={onCountryChange}
        placeholder={gettext('Phone number')}
        labels={labels}
        locales={language.code}
        countrySelectComponent={PhoneCountrySelect}
        value={value || undefined}
        onChange={(phone) => onChange(phone || '')}
        onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
          const phone = event.clipboardData.getData('text').trim();
          if (phone.startsWith('00')) {
            event.preventDefault();
            onChange(`+${phone.slice(2).replace(/\D/g, '')}`);
          }
        }}
        autoComplete="tel"
        aria-invalid={Boolean(error)}
        aria-describedby={error ? errorId : undefined}
        numberInputProps={{
          className:
            'min-w-0 flex-1 rounded-r-xl border-0 bg-transparent px-3 text-base text-black outline-none placeholder:text-retino-grey/70 focus:ring-0 sm:text-sm',
        }}
        className={cx(
          'relative flex h-12 rounded-xl border bg-white transition focus-within:border-retino-darkviolet focus-within:ring-2 focus-within:ring-retino-lightviolet',
          error ? 'border-red-500' : 'border-slate-300',
        )}
      />
      {error && (
        <p id={errorId} className="mt-2 text-[13px] leading-snug text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}

export default PhoneInput;
